import type {
  FleetActivityAgent,
  FleetActivitySnapshot,
  FleetActivityThresholds,
} from '../../api/types';
import {
  agentKey,
  delegationEdges,
  humanSeconds,
  origenesDeAgente,
  plural,
  type DelegationEdge,
  type Pulse,
  type PulseMap,
  type OrigenEncargo,
} from './agent-state-helpers';
import { PROVIDER_LOGIN_REQUIRED_MESSAGE } from './provider-login';

/**
 * The eight states the console distinguishes, each with its own doll. The order of the union
 * is the precedence: `down` wins over everything, `idle` loses to everything.
 *
 * A live lease does not prove the agent responds: `blocked` is derived from stall signals
 * (`work_state: 'stalled'`, `ack_stalled`, `overdue_acks`), and `down` is declared when the
 * lease has expired or there was never a connection.
 */
export type LiveState =
  | 'down'
  | 'blocked'
  | 'delegating'
  | 'settled'
  | 'receiving'
  | 'thinking'
  | 'idle'
  | 'unprepared';

export const LIVE_STATES: readonly LiveState[] = [
  'down', 'blocked', 'delegating', 'settled', 'receiving', 'thinking', 'idle', 'unprepared',
];

interface LiveStateMeta {
  label: string;
  /** One line, in Spanish, explaining what is happening without database jargon. */
  hint: string;
}

export const LIVE_STATE_META: Record<LiveState, LiveStateMeta> = {
  down: { label: 'Caído', hint: 'Sin conexión vigente o nunca conectó: nadie va a tomar su trabajo.' },
  blocked: { label: 'Trabado', hint: 'Tomó trabajo y no avanza. Es el fallo que se ve como "tarda", no como error.' },
  delegating: { label: 'Delegando', hint: 'Le pasó trabajo a otro agente, que ya lo tiene en vuelo.' },
  settled: {
    label: 'Salió de vuelo',
    hint: 'Una entrega suya dejó de estar en vuelo. Si cerró bien o se murió NO se puede saber desde la consola.',
  },
  receiving: { label: 'Recibiendo', hint: 'Le entró trabajo nuevo y todavía no empezó el turno.' },
  thinking: { label: 'Trabajando', hint: 'Turno en curso: el arnés está masticando la entrega.' },
  idle: {
    label: 'Libre',
    hint: 'Nada en vuelo. En el mapa, además, con conexión vigente: un alias sin trabajo Y sin conexión se dibuja Caído, que gana.',
  },
  unprepared: {
    label: 'Sin preparar',
    hint: 'Sigue en borrador: su entorno todavía no está preparado para trabajar. No es una caída.',
  },
};

/** How long a transient state stays on screen before falling to the stable state. */
export const BURST_MS = 4500;

export interface LiveAgentView {
  key: string;
  tenantId: string;
  alias: string;
  displayName?: string | null;
  harnessId?: string | null;
  state: LiveState;
  /** Concrete and verifiable reason for the state, for the tooltip and the screen reader. */
  reason: string;
  /** `working` with `in_flight >= saturation_in_flight`: the doll is thinking, but overheated. */
  overloaded: boolean;
  inFlight: number;
  queued: number;
  oldestInFlightSeconds?: number | null;
  oldestClaimedNotStartedSeconds?: number | null;
  oldestClaimedNotStartedWithoutAckSeconds?: number | null;
  oldestClaimedNotStartedActivitySeconds?: number | null;
  claimedNotStartedNeverAcked?: boolean;
  secondsSinceLastAck?: number | null;
  delegatesTo: string[];
  delegatedFrom: string[];
  flags: string[];
  /**
   * Deliveries closed in the last 24 h, or `undefined` if the server does not report it.
   */
  closed24h?: number;
  rooms: string[];
  origenes: OrigenEncargo[];
  agent: FleetActivityAgent;
}

interface LiveStateContext {
  pulses?: readonly Pulse[];
  delegatesTo?: readonly string[];
  thresholds?: FleetActivityThresholds | null;
  nowMs: number;
}

/**
 * The doll's state. Explicit precedence, top to bottom, and each branch states WHY it decided
 * that: if a doll lies tomorrow, the reason says which field to contrast it against.
 */
export function liveState(
  agent: FleetActivityAgent,
  context: LiveStateContext,
): { state: LiveState; reason: string; overloaded: boolean } {
  const decidido = decidirEstado(agent, context);
  const flags = agent.flags ?? [];
  const sinRegistro = flags.includes('unregistered') || agent.registered === false;
  if (!sinRegistro) return decidido;
  return {
    ...decidido,
    reason: `${decidido.reason} No está en el registro de agentes: apareció por entregas o por una conexión.`,
  };
}

function decidirEstado(
  agent: FleetActivityAgent,
  context: LiveStateContext,
): { state: LiveState; reason: string; overloaded: boolean } {
  const flags = agent.flags ?? [];
  const saturation = context.thresholds?.saturation_in_flight ?? 8;
  const stallAfter = context.thresholds?.stall_after_seconds ?? 300;
  const startAfter = context.thresholds?.start_after_seconds ?? 60;
  const inFlight = agent.in_flight ?? 0;
  const overloaded = inFlight >= saturation;

  const sinRegistro = flags.includes('unregistered') || agent.registered === false;

  if (nuncaPreparado(agent)) {
    return {
      state: 'unprepared',
      reason: agent.host_id
        ? 'Sigue en borrador y su entorno todavía no está preparado. Prepáralo para que pueda trabajar.'
        : 'Nació en borrador y todavía no tiene ordenador. No es una caída: prepáralo para que pueda trabajar.',
      overloaded,
    };
  }
  if (agent.agent_enabled === false && !sinRegistro) {
    return { state: 'down', reason: 'Deshabilitado en el registro de agentes: no se le asigna trabajo.', overloaded };
  }
  if (flags.includes('never_connected')) {
    return { state: 'down', reason: 'Nunca se ha conectado este alias.', overloaded };
  }
  if (flags.includes('lease_expired') || agent.presence?.online === false) {
    return { state: 'down', reason: 'La conexión venció. El adaptador no está sosteniendo la conexión.', overloaded };
  }

  if (agent.provider_login_required === true) {
    return { state: 'blocked', reason: PROVIDER_LOGIN_REQUIRED_MESSAGE, overloaded };
  }

  if (flags.includes('overdue_acks')) {
    return { state: 'blocked', reason: 'Tiene entregas con el plazo de confirmación vencido: el turno se le está muriendo.', overloaded };
  }
  if (flags.includes('ack_stalled')) {
    const since = agent.seconds_since_last_ack;
    return {
      state: 'blocked',
      reason: since === null || since === undefined
        ? 'Trabajo en vuelo y ninguna confirmación dentro de la ventana de búsqueda: la señal más grave.'
        : `Trabajo en vuelo y la última confirmación fue hace ${humanSeconds(since)}.`,
      overloaded,
    };
  }
  if (flags.includes('claimed_not_started')) {
    const withoutAckAge = agent.oldest_claimed_not_started_without_ack_seconds;
    if (typeof withoutAckAge === 'number' && withoutAckAge > startAfter) {
      return {
        state: 'blocked',
        reason: `Tomó trabajo hace ${humanSeconds(withoutAckAge)} y nunca registró una confirmación propia.`,
        overloaded,
      };
    }
    const activityAge = agent.oldest_claimed_not_started_activity_seconds
      ?? agent.oldest_claimed_not_started_seconds;
    const desde = typeof activityAge === 'number' ? ` hace ${humanSeconds(activityAge)}` : '';
    return {
      state: 'blocked',
      reason: `Tomó trabajo y no registra actividad propia${desde}: no está empezando esa entrega.`,
      overloaded,
    };
  }
  if (agent.work_state === 'stalled') {
    const age = agent.oldest_in_flight_seconds;
    return {
      state: 'blocked',
      reason: typeof age === 'number'
        ? `Tomó una entrega hace ${humanSeconds(age)} y no la cerró (umbral: ${humanSeconds(stallAfter)}).`
        : 'El servidor lo marcó estancado: trabajo tomado que no avanza.',
      overloaded,
    };
  }
  if (flags.includes('queued_without_consumer') && agent.presence?.online !== true) {
    return { state: 'blocked', reason: 'Tiene cola encolada y ningún consumidor conectado que la tome.', overloaded };
  }
  // Conectado y sin nada en vuelo, pero con entregas que ya podría tomar: no se oculta como
  // «Recibiendo». Un consumidor sin la capacidad que pide la entrega la filtra para siempre.
  if (flags.includes('queued_without_consumer') && agent.queued_ready !== 0) {
    const ready = agent.queued_ready;
    const cuantas = typeof ready === 'number'
      ? plural(ready, 'entrega disponible', 'entregas disponibles')
      : plural(agent.queued ?? 0, 'entrega', 'entregas');
    return {
      state: 'blocked',
      reason: `Conectado, pero no tomó ${cuantas} y no tiene nada en vuelo: `
        + 'su consumidor puede no tener la capacidad que piden.',
      overloaded,
    };
  }

  const pulses = context.pulses ?? [];
  const received = pulses.find((pulse) => pulse.kind === 'received');
  const settled = pulses.find((pulse) => pulse.kind === 'settled' && pulse.outcome === 'deadline_vencido')
    ?? pulses.find((pulse) => pulse.kind === 'settled');

  if (settled && context.nowMs - settled.atMs < BURST_MS) {
    return {
      state: 'settled',
      reason: settled.outcome === 'deadline_vencido'
        ? 'Una entrega suya salió de vuelo con el plazo de confirmación ya vencido: no fue un cierre limpio.'
        : 'Una de sus entregas dejó de figurar entre las activas durante la última actualización. '
          + 'Esta vista no permite confirmar si terminó, falló o venció.',
      overloaded,
    };
  }

  const delegatesTo = context.delegatesTo ?? [];
  if (delegatesTo.length > 0) {
    return {
      state: 'delegating',
      reason: `Le pasó trabajo a ${delegatesTo.map((key) => key.split('/')[1]).join(', ')}, que lo tiene en vuelo.`,
      overloaded,
    };
  }

  if (received && context.nowMs - received.atMs < BURST_MS) {
    return { state: 'receiving', reason: 'Le entró una entrega nueva en el último refresco.', overloaded };
  }

  const claimedNotStarted = agent.claimed_not_started ?? 0;
  if (claimedNotStarted > 0 && (agent.started ?? 0) === 0) {
    return { state: 'receiving', reason: `${plural(claimedNotStarted, 'entrega tomada', 'entregas tomadas')} y todavía sin empezar.`, overloaded };
  }

  if (agent.work_state === 'working' || agent.work_state === 'saturated' || inFlight > 0) {
    return {
      state: 'thinking',
      reason: overloaded
        ? `${plural(inFlight, 'entrega', 'entregas')} en vuelo: por encima del umbral de saturación (${String(saturation)}).`
        : `${plural(inFlight, 'entrega', 'entregas')} en vuelo.`,
      overloaded,
    };
  }

  if ((agent.queued ?? 0) > 0) {
    const ready = agent.queued_ready;
    const availability = typeof ready !== 'number'
      ? 'El servidor no informa cuántas están disponibles por horario.'
      : ready === 0
        ? 'Ninguna está disponible por horario todavía.'
        : `${plural(ready, 'disponible por horario', 'disponibles por horario')}.`;
    return {
      state: 'receiving',
      reason: `Conectado, con ${plural(agent.queued ?? 0, 'entrega esperando', 'entregas esperando')} en cola sin tomar. `
        + `${availability} Este dato no confirma que pueda tomarlas ni prueba una ejecución trabada.`,
      overloaded,
    };
  }

  return { state: 'idle', reason: 'Conectado y nada en vuelo.', overloaded };
}

function nuncaPreparado(agent: FleetActivityAgent): boolean {
  const flags = agent.flags ?? [];
  if (flags.includes('unregistered') || agent.registered === false) return false;
  return agent.lifecycle_state === 'draft' && agent.agent_enabled !== true;
}

/** Count per state, for the summary bar. Always publishes every state, even at zero. */
export function stateTally(views: readonly LiveAgentView[]): Record<LiveState, number> {
  const tally = Object.fromEntries(LIVE_STATES.map((state) => [state, 0])) as Record<LiveState, number>;
  for (const view of views) tally[view.state] += 1;
  return tally;
}

export function buildLiveViews(
  snapshot: FleetActivitySnapshot | undefined,
  pulses: PulseMap,
  nowMs: number,
): { views: LiveAgentView[]; edges: DelegationEdge[] } {
  const edges = delegationEdges(snapshot);
  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();
  for (const edge of edges) {
    outgoing.set(edge.from, [...(outgoing.get(edge.from) ?? []), edge.to]);
    incoming.set(edge.to, [...(incoming.get(edge.to) ?? []), edge.from]);
  }

  const agents = snapshot?.agents ?? [];
  const known = new Set(agents.map(agentKey));
  const startAfter = snapshot?.thresholds?.start_after_seconds ?? 60;

  const views = agents.map((agent) => {
    const key = agentKey(agent);
    const delegatesTo = [...new Set(outgoing.get(key) ?? [])];
    const { state, reason, overloaded } = liveState(agent, {
      pulses: pulses[key],
      delegatesTo,
      thresholds: snapshot?.thresholds,
      nowMs,
    });
    return {
      key,
      tenantId: agent.tenant_id,
      alias: agent.alias,
      displayName: agent.display_name,
      harnessId: agent.harness_id,
      state,
      reason,
      overloaded,
      inFlight: agent.in_flight ?? 0,
      queued: agent.queued ?? 0,
      oldestInFlightSeconds: agent.oldest_in_flight_seconds,
      oldestClaimedNotStartedSeconds: agent.oldest_claimed_not_started_seconds,
      oldestClaimedNotStartedWithoutAckSeconds: agent.oldest_claimed_not_started_without_ack_seconds,
      oldestClaimedNotStartedActivitySeconds: agent.oldest_claimed_not_started_activity_seconds,
      claimedNotStartedNeverAcked:
        typeof agent.oldest_claimed_not_started_without_ack_seconds === 'number'
        && agent.oldest_claimed_not_started_without_ack_seconds > startAfter,
      secondsSinceLastAck: agent.seconds_since_last_ack,
      delegatesTo,
      delegatedFrom: [...new Set(incoming.get(key) ?? [])],
      flags: agent.flags ?? [],
      closed24h: typeof agent.closed_24h === 'number' ? agent.closed_24h : undefined,
      rooms: agent.rooms ?? [],
      origenes: origenesDeAgente(agent, known),
      agent,
    } satisfies LiveAgentView;
  });

  return { views, edges };
}

// ==============================================================================================
// The verdict, and what is needed to be able to issue it honestly.
// ==============================================================================================

interface VerdictCulprit {
  key: string;
  alias: string;
  /** Short and verifiable phrase: "stuck 22 min ago", "down". It is what goes inside the chip. */
  motivo: string;
}

export type Verdict =
  | { tone: 'ok' | 'alerta'; culpables: VerdictCulprit[] }
  | { tone: 'desconocido'; frase: string; apoyo: string; culpables: VerdictCulprit[] };

interface VerdictInput {
  /** The last read failed. Enough on its own to make the verdict NOT green. */
  error?: Error | null;
  /** `observed_at` of the snapshot being shown. */
  observedAt?: string | null;
  nowMs: number;
  /** From what age the snapshot stops proving anything. */
  staleAfterMs: number;
}

function motivoDe(view: LiveAgentView): string {
  if (view.state === 'down') return 'caído';
  const enVuelo = typeof view.oldestInFlightSeconds === 'number'
    ? `; entrega en vuelo hace ${humanSeconds(view.oldestInFlightSeconds)}`
    : '';
  if (view.flags.includes('overdue_acks')) return `trabado: confirmación vencida${enVuelo}`;
  if (view.flags.includes('ack_stalled')) {
    const ack = view.secondsSinceLastAck;
    return ack === null || ack === undefined
      ? `trabado: sin confirmación reciente${enVuelo}`
      : `trabado: última confirmación hace ${humanSeconds(ack)}${enVuelo}`;
  }
  if (view.flags.includes('claimed_not_started')) {
    if (view.claimedNotStartedNeverAcked) {
      const age = view.oldestClaimedNotStartedWithoutAckSeconds;
      return typeof age === 'number'
        ? `trabado: tomó y no empezó; nunca confirmó por su cuenta en ${humanSeconds(age)}`
        : 'trabado: tomó y no empezó; nunca confirmó por su cuenta';
    }
    const age = view.oldestClaimedNotStartedActivitySeconds
      ?? view.oldestClaimedNotStartedSeconds;
    return typeof age === 'number'
      ? `trabado: tomó y no empezó; última confirmación propia hace ${humanSeconds(age)}`
      : 'trabado: tomó y no empezó';
  }
  if (view.flags.includes('queued_without_consumer')) return 'trabado: cola sin consumidor';
  return `trabado según el servidor${enVuelo}`;
}

function horaCorta(marca: number): string {
  return new Intl.DateTimeFormat('es', { hour: '2-digit', minute: '2-digit' }).format(new Date(marca));
}

export function fleetVerdict(views: readonly LiveAgentView[], input: VerdictInput): Verdict {
  const marca = typeof input.observedAt === 'string' ? Date.parse(input.observedAt) : Number.NaN;
  const edadMs = Number.isFinite(marca) ? input.nowMs - marca : Number.NaN;
  const rancio = !Number.isFinite(edadMs) || edadMs > input.staleAfterMs;

  if (input.error || rancio) {
    const desde = Number.isFinite(marca)
      ? `Datos de hace ${humanSeconds(Math.max(0, edadMs) / 1000)} (última lectura buena ${horaCorta(marca)}).`
      : 'Todavía no llegó ninguna lectura con hora del servidor.';
    return {
      tone: 'desconocido',
      frase: input.error ? 'No lo sé: la última lectura falló.' : 'No lo sé: el dato está viejo.',
      apoyo: desde,
      culpables: [],
    };
  }

  if (views.length === 0) {
    return {
      tone: 'desconocido',
      frase: 'No lo sé: no hay ni un alias que mirar.',
      apoyo: 'La lectura llegó fresca pero no trae ningún agente en este alcance. Cero mediciones no es lo mismo que cero problemas.',
      culpables: [],
    };
  }

  const culpables = views
    .filter((view) => view.state === 'down' || view.state === 'blocked')
    .map((view) => ({ key: view.key, alias: view.alias, motivo: motivoDe(view) }));
  return culpables.length > 0 ? { tone: 'alerta', culpables } : { tone: 'ok', culpables: [] };
}

export {
  agentKey,
  aliasDe,
  humanSeconds,
  delegationEdges,
  type DelegationEdge,
  type ItemMemory,
  type AgentMemory,
  type FleetMemory,
  rememberFleet,
  type PulseOutcome,
  type Pulse,
  type PulseMap,
  detectPulses,
  type OrigenEncargo,
  origenDeItem,
} from './agent-state-helpers';
